import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { lawTopicsFR } from '@/i18n/lawTopics'

/**
 * Composable for translating law topics based on the current locale
 *
 * :return: Object containing translateLawTopic function
 */
export function useLawTopicTranslation() {
  const { locale } = useI18n()

  /**
   * Translate a law topic based on the current locale
   *
   * :param lawTopic: The law topic key to translate (e.g., 'KÜNDIGUNG')
   * :return: Translated law topic or original value if no translation exists
   */
  const translateLawTopic = (lawTopic: string | null | undefined): string => {
    if (!lawTopic) return ''

    // Access locale.value to ensure reactivity
    const currentLocale = locale.value

    // Apply French translations when locale is French, fallback to German key
    if (currentLocale === 'fr' && lawTopicsFR[lawTopic]) {
      return lawTopicsFR[lawTopic]
    }

    // Fall back to raw key (German) if no translation exists
    return lawTopic
  }

  /**
   * Computed property that returns translations map based on locale
   * Useful for reactive translations in computed properties
   */
  const lawTopicTranslations = computed(() => {
    if (locale.value === 'fr') return lawTopicsFR
    return {} // Empty for non-French locales (use German keys)
  })

  return {
    translateLawTopic,
    lawTopicTranslations
  }
}
